import type { ReactNode } from 'react';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import clsx from 'clsx';
import { PageHeaderBreadcrumb, PageHeaderBreadcrumbLink } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { useHasProjectPermission } from '@/entities/user';
import { useApprovalChainsEnabled } from '@/shared/hooks/useApprovalChainsEnabled';
import { useModalState } from '@/shared/hooks/useModalState';
import { CreateFolderModal, FolderBriefBadge } from '@/features/manage-project-folders';
import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';
import { ApprovalProgressBadge } from '../ApprovalProgressBadge';
import { FolderApprovalChainTags } from '../FolderApprovalChainTags';
import { FolderActionsMenu } from '../FolderActionsMenu';
import { RailToggleButton } from '../RailToggleButton';

interface FolderPaneHeaderProps {
  projectId: string;
  folder: FolderResponseDto;
  onDeleted: () => void;
  /**
   * The count rolls up the whole subtree, so a folder of folders would report
   * assets it does not itself hold. Only the folder showing them says so.
   */
  showApprovalProgress: boolean;
  /** Sits at the far end of the header row; a folder of folders offers one. */
  actions?: ReactNode;
}

/**
 * The open folder's name, its place in the tree, and its approval standing.
 *
 * The path reads as an overline above the name rather than a trail ending in
 * it: the folder the assets below belong to is the heading, so the crumbs are
 * only its ancestors and every one of them is a link back out.
 */
export function FolderPaneHeader(props: FolderPaneHeaderProps) {
  const { t } = useTranslation('projects');
  const { folder } = props;

  // The header owns the editor rather than the actions menu, so the title, the
  // menu item and the brief badge open the same one. Renaming answers to the
  // permission that guards every other folder mutation; without it the title
  // is text.
  const canEditFolder = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);
  const editModal = useModalState();
  const ancestors = folder.breadcrumbs;

  // The chain tags already name each chain AND carry its count, so the
  // aggregate pill only repeats them. It stands in only where the chains
  // cannot be drawn: the chains are derived from the folder's own files, so a
  // folder of folders has none to derive and its count is a rollup of a
  // subtree this never loads.
  const approvalChainsEnabled = useApprovalChainsEnabled();
  const hasSubfolders = folder.foldersCount > 0;
  const showChainTags =
    approvalChainsEnabled &&
    (folder.approvalProgress.totalCount > 0 || !!folder.autoApplyWorkflowId);

  // A fixed row, not a minimum: the name and its path overline are taller than
  // this and overflow it evenly, which is what keeps the toggle at one height
  // whether the folder has ancestors or not. A minimum would let the two-line
  // case grow the row and move the button with it.
  return (
    <header className="flex h-10 items-center gap-md">
      <RailToggleButton />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The overline carries the path alone. The brief is a badge in the
            row below, beside the name it belongs to. */}
        {ancestors.length > 0 && (
          <PageHeaderBreadcrumb size="xs">
            {ancestors.map((ancestor) => (
              <PageHeaderBreadcrumbLink
                key={ancestor.id}
                href={ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, ancestor.id)}
              >
                {ancestor.name}
              </PageHeaderBreadcrumbLink>
            ))}
          </PageHeaderBreadcrumb>
        )}

        <div className="flex min-w-0 items-center gap-sm">
          <div className="flex min-w-0 items-center gap-3xs">
            {/* A shortcut to the menu item, not a second way to rename: an
                affordance of its own here would sit against the actions
                chevron, and the two would compete for the same corner. */}
            <h1
              onDoubleClick={canEditFolder ? editModal.open : undefined}
              className={clsx(
                'min-w-0 truncate text-heading-lg font-medium text-foreground',
                canEditFolder && 'cursor-pointer',
              )}
            >
              {folder.name}
            </h1>
            <FolderActionsMenu
              projectId={props.projectId}
              folder={folder}
              onEdit={editModal.open}
              onDeleted={props.onDeleted}
              approvalChainsEnabled={approvalChainsEnabled}
            />
          </div>

          {props.showApprovalProgress && !showChainTags && (
            <ApprovalProgressBadge progress={folder.approvalProgress} />
          )}

          {showChainTags && (
            <FolderApprovalChainTags
              projectId={props.projectId}
              folderId={folder.id}
              folderProgress={folder.approvalProgress}
              deriveChains={!hasSubfolders && folder.fileCount > 0}
              newUploadsApprovalChainId={folder.autoApplyWorkflowId}
            />
          )}

          <FolderBriefBadge
            projectId={props.projectId}
            folder={folder}
            onAddBrief={editModal.open}
          />
        </div>
      </div>

      {props.actions}

      {editModal.isMounted && (
        <CreateFolderModal
          key={editModal.instanceKey}
          platformProjectId={props.projectId}
          mode="edit"
          folder={folder}
          open={editModal.isOpen}
          onClose={editModal.close}
          onSuccess={(updated) =>
            notificationService.showSuccess(
              t('projectDetail.folderPaneHeader.folderRenamed', { folderName: updated.name }),
            )
          }
        />
      )}
    </header>
  );
}
